import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Home } from 'lucide-react'

interface BreadcrumbItem {
  label: string
  path: string
}

export function Breadcrumb() {
  const location = useLocation()
  const { t: tc } = useTranslation('common')
  const pathnames = location.pathname.split('/').filter((x) => x)

  // Route name mapping
  const routeNames: Record<string, string> = {
    '': tc('breadcrumbDashboard'),
    'calendar': tc('breadcrumbCalendar'),
    'catalog': tc('breadcrumbSessionCatalog'),
    'conferences': tc('breadcrumbConferences'),
    'hybrid-events': tc('breadcrumbHybrid'),
    'ondemand': tc('breadcrumbOnDemand'),
    'cart': tc('breadcrumbShoppingCart'),
    'checkout': tc('breadcrumbCheckout'),
    'registration-history': tc('breadcrumbRegistrationCertificate'),
    'user-profile': tc('breadcrumbProfile'),
    'users': tc('breadcrumbUsers'),
    'settings': tc('breadcrumbSettings'),
    'session': tc('breadcrumbSessionDetails'),
    'transcript': tc('breadcrumbTranscript'),
    'workforce-development-wednesday': tc('breadcrumbWorkforceDevelopmentWednesday'),
    'internal-sessions': tc('breadcrumbInternalSessions')
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { label: tc('breadcrumbHome'), path: '/' }
  ]

  // Special handling for session detail pages
  if (pathnames[0] === 'session' && pathnames[1] && /^\d+$/.test(pathnames[1])) {
    breadcrumbs.push({ label: tc('breadcrumbSessionCatalog'), path: '/catalog' })
    breadcrumbs.push({ label: tc('breadcrumbSessionNumber', { id: pathnames[1] }), path: location.pathname })
  } else if (pathnames[0] === 'conferences' && pathnames[1] && /^\d+$/.test(pathnames[1])) {
    breadcrumbs.push({ label: tc('breadcrumbConferences'), path: '/conferences' })
    breadcrumbs.push({ label: tc('breadcrumbEventNumber', { id: pathnames[1] }), path: location.pathname })
  } else if (pathnames[0] === 'hybrid-events' && pathnames[1] && /^\d+$/.test(pathnames[1])) {
    breadcrumbs.push({ label: tc('breadcrumbHybrid'), path: '/hybrid-events' })
    breadcrumbs.push({ label: tc('breadcrumbEventNumber', { id: pathnames[1] }), path: location.pathname })
  } else {
    // Default breadcrumb logic
    let currentPath = ''
    pathnames.forEach((pathname, index) => {
      currentPath += `/${pathname}`
      // Skip dynamic segments that are numbers (like session IDs)
      if (!/^\d+$/.test(pathname)) {
        breadcrumbs.push({
          label: routeNames[pathname] || pathname.charAt(0).toUpperCase() + pathname.slice(1),
          path: currentPath
        })
      } else if (index > 0) {
        // For IDs, show them but don't make them clickable
        breadcrumbs[breadcrumbs.length - 1].label += ` #${pathname}`
      }
    })
  }

  // Don't show breadcrumb on homepage
  if (breadcrumbs.length === 1) {
    return null
  }

  return (
    <nav className="flex items-center space-x-1 text-sm mb-4 px-1" aria-label={tc('breadcrumb')}>
      {breadcrumbs.map((crumb, index) => (
        <div key={crumb.path} className="flex items-center">
          {index > 0 && (
            <ChevronRight className="h-4 w-4 mx-2" style={{ color: '#64748B' }} />
          )}
          {index === 0 && (
            <Home className="h-4 w-4 mr-2" style={{ color: '#086296' }} />
          )}
          {index === breadcrumbs.length - 1 ? (
            <span className="font-semibold px-2 py-1 rounded-md" style={{ color: '#052B3D', backgroundColor: '#F1F5F9' }}>
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.path}
              className="hover:bg-blue-50 transition-colors px-2 py-1 rounded-md font-medium"
              style={{ color: '#086296' }}
            >
              {crumb.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  )
}
